import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Alert,
  LinearProgress,
  Grid,
  Card,
  CardContent,
  IconButton,
  InputAdornment,
  TextField,
  Autocomplete,
} from '@mui/material';
import {
  Close,
  Devices,
  CheckCircle,
  Warning,
  Block,
  Search,
  Clear,
  SwapHoriz,
  Map as MapIcon,
} from '@mui/icons-material';
import { ftthCaixaService } from '../../services/ftthCaixaService';
import equipmentService from '../../services/equipmentService';
import circuitService, { Circuito } from '../../services/circuitService';
import EscritorioSelect from '../selects/EscritorioSelect';
import { Equipment } from '../../types';
import { fetchCaixaPorts } from '../../utils/loadCaixaPorts';
import { CaixaPort } from '../../types/caixaPort';
import CaixaMapComponent from '../../pages/ftth/components/CaixaMapComponent';

interface FtthCaixa {
  id: number;
  name: string;
  address: string;
  latitude?: number;
  longitude?: number;
  capacidade: number;
  disabled_ports: Array<{
    port: number;
    reason: string;
    date: string;
    usuarioId?: number;
  }> | Record<string, {
    reason: string;
    date: string;
    usuarioId?: number;
  }> | null;
  status: 'ativo' | 'inativo' | 'aguardando_instalacao' | 'manutencao';
}

type FtthCaixaPort = CaixaPort;

type TrocarCtoEquipment = Equipment & {
  caixa?: {
    id: number;
    name: string;
    olt_id?: number;
  };
};

interface TrocarCtoModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  equipment: TrocarCtoEquipment | null;
  contractId: number;
}

interface CaixaMapInfo {
  name: string;
  address: string;
  latitude: number;
  longitude: number;
}

const TrocarCtoModal: React.FC<TrocarCtoModalProps> = ({
  open,
  onClose,
  onSuccess,
  equipment,
  contractId,
}) => {
  const [selectedEscritorioId, setSelectedEscritorioId] = useState<number | ''>('');
  const [caixas, setCaixas] = useState<FtthCaixa[]>([]);
  const [selectedCaixaId, setSelectedCaixaId] = useState<number | ''>('');
  const [caixaPorts, setCaixaPorts] = useState<FtthCaixaPort[]>([]);
  const [selectedPort, setSelectedPort] = useState<number | ''>('');
  const [circuitos, setCircuitos] = useState<Circuito[]>([]);
  const [selectedCircuitoOption, setSelectedCircuitoOption] = useState<Circuito | string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingCaixas, setLoadingCaixas] = useState(false);
  const [loadingPorts, setLoadingPorts] = useState(false);
  const [loadingCircuitos, setLoadingCircuitos] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [caixaSearch, setCaixaSearch] = useState('');
  const [currentCaixaCoords, setCurrentCaixaCoords] = useState<CaixaMapInfo | null>(null);
  const [novaCaixaCoords, setNovaCaixaCoords] = useState<CaixaMapInfo | null>(null);
  const [mapModalOpen, setMapModalOpen] = useState(false);
  const [mapView, setMapView] = useState<CaixaMapInfo | null>(null);

  const openMap = (info: CaixaMapInfo) => {
    setMapView(info);
    setMapModalOpen(true);
  };

  useEffect(() => {
    if (open && equipment) {
      setSelectedEscritorioId('');
      setSelectedCaixaId('');
      setSelectedPort('');
      setSelectedCircuitoOption(null);
      setCaixas([]);
      setCaixaPorts([]);
      setCircuitos([]);
      setError(null);
      setCaixaSearch('');
      setCurrentCaixaCoords(null);
      setNovaCaixaCoords(null);
      setMapModalOpen(false);
      setMapView(null);
    }
  }, [open, equipment]);

  useEffect(() => {
    if (!open || !equipment?.caixa_id) {
      setCurrentCaixaCoords(null);
      return;
    }

    let cancelled = false;
    const loadCurrentCaixaCoords = async () => {
      try {
        const response = await ftthCaixaService.getById(equipment.caixa_id!);
        if (cancelled) return;
        const caixa = response.data;
        if (caixa?.latitude && caixa?.longitude) {
          setCurrentCaixaCoords({
            name: caixa.name || equipment.caixa?.name || equipment.caixa_name || 'CTO',
            address: caixa.address || '',
            latitude: Number(caixa.latitude),
            longitude: Number(caixa.longitude),
          });
        } else {
          setCurrentCaixaCoords(null);
        }
      } catch {
        if (!cancelled) setCurrentCaixaCoords(null);
      }
    };

    loadCurrentCaixaCoords();
    return () => {
      cancelled = true;
    };
  }, [open, equipment]);

  useEffect(() => {
    if (selectedEscritorioId) {
      setCaixas([]);
      setSelectedCaixaId('');
      setSelectedPort('');
      setSelectedCircuitoOption(null);
      setCaixaPorts([]);
      setCircuitos([]);
      setCaixaSearch('');
      loadCaixas(selectedEscritorioId as number);
    } else {
      setCaixas([]);
      setSelectedCaixaId('');
      setSelectedPort('');
      setSelectedCircuitoOption(null);
      setCaixaPorts([]);
      setCircuitos([]);
    }
  }, [selectedEscritorioId]);

  useEffect(() => {
    if (selectedCaixaId) {
      loadCaixaPorts(selectedCaixaId as number);
      loadCircuitos(selectedCaixaId as number);
    } else {
      setCaixaPorts([]);
      setSelectedPort('');
      setCircuitos([]);
      setSelectedCircuitoOption(null);
    }
  }, [selectedCaixaId]);

  useEffect(() => {
    if (!selectedCaixaId) {
      setNovaCaixaCoords(null);
      return;
    }

    const caixaFromList = caixas.find((caixa) => caixa.id === selectedCaixaId);
    if (caixaFromList?.latitude && caixaFromList?.longitude) {
      setNovaCaixaCoords({
        name: caixaFromList.name,
        address: caixaFromList.address,
        latitude: Number(caixaFromList.latitude),
        longitude: Number(caixaFromList.longitude),
      });
      return;
    }

    let cancelled = false;
    const loadNovaCaixaCoords = async () => {
      try {
        const response = await ftthCaixaService.getById(selectedCaixaId as number);
        if (cancelled) return;
        const caixa = response.data;
        if (caixa?.latitude && caixa?.longitude) {
          setNovaCaixaCoords({
            name: caixa.name,
            address: caixa.address || '',
            latitude: Number(caixa.latitude),
            longitude: Number(caixa.longitude),
          });
        } else {
          setNovaCaixaCoords(null);
        }
      } catch {
        if (!cancelled) setNovaCaixaCoords(null);
      }
    };

    loadNovaCaixaCoords();
    return () => {
      cancelled = true;
    };
  }, [selectedCaixaId, caixas]);

  const filteredCaixas = React.useMemo(() => {
    return caixas.filter(caixa =>
      caixa.name.toLowerCase().includes(caixaSearch.toLowerCase()) ||
      caixa.address.toLowerCase().includes(caixaSearch.toLowerCase())
    );
  }, [caixas, caixaSearch]);

  const loadCaixas = async (escritorioId: number) => {
    try {
      setLoadingCaixas(true);
      setError(null);

      const response = await ftthCaixaService.getCaixas({
        limit: 2000,
        tipo: 'cto',
        status: 'ativo',
        escritorio_id: escritorioId
      });

      let caixasData: FtthCaixa[] = [];

      if (response && response.success && response.data && response.data.caixas) {
        caixasData = response.data.caixas;
      } else if (response && response.caixas) {
        caixasData = response.caixas;
      } else if (response && Array.isArray(response.data)) {
        caixasData = response.data;
      } else if (Array.isArray(response)) {
        caixasData = response;
      }

      setCaixas(caixasData);
    } catch (err: any) {
      console.error('Erro ao carregar CAIXAs:', err);
      setCaixas([]);
      setError(`Erro ao carregar CAIXAs: ${err.message}`);
    } finally {
      setLoadingCaixas(false);
    }
  };

  const loadCaixaPorts = async (caixaId: number) => {
    try {
      setLoadingPorts(true);
      setError(null);

      const ports = await fetchCaixaPorts(caixaId, {
        excludeEquipmentId: equipment?.id
      });
      setCaixaPorts(ports);
    } catch (err: any) {
      console.error('Erro ao carregar portas:', err);
      setError(err.message || 'Erro ao carregar portas da CAIXA');

      const selectedCaixa = caixas.find((caixa) => caixa.id === caixaId);
      if (selectedCaixa) {
        const basicPorts: FtthCaixaPort[] = [];
        const totalPorts = selectedCaixa.capacidade || 0;
        for (let i = 1; i <= totalPorts; i++) {
          basicPorts.push({ port_number: i, status: 'available' });
        }
        setCaixaPorts(basicPorts);
      }
    } finally {
      setLoadingPorts(false);
    }
  };

  const loadCircuitos = async (caixaId: number) => {
    try {
      setLoadingCircuitos(true);
      setError(null);

      const response = await circuitService.getCircuitos({
        caixa_id: caixaId,
        situacao: 'livre',
        limit: 100
      });

      setCircuitos(response.circuitos || []);
    } catch (err: any) {
      console.error('Erro ao carregar circuitos:', err);
      setError(`Erro ao carregar circuitos: ${err.message}`);
      setCircuitos([]);
    } finally {
      setLoadingCircuitos(false);
    }
  };

  const getPortLabel = (port: FtthCaixaPort) => {
    switch (port.status) {
      case 'available':
        return `Porta ${port.port_number} - Disponível`;
      case 'occupied':
        return `Porta ${port.port_number} - ${port.info?.client_name || 'Cliente'} (${port.info?.contract_title || ''})`;
      case 'disabled':
        return `Porta ${port.port_number} - Desabilitada (${port.info?.reason || 'Sem motivo'})`;
      default:
        return `Porta ${port.port_number}`;
    }
  };

  const getPortIcon = (status: string) => {
    switch (status) {
      case 'available':
        return <CheckCircle color="success" />;
      case 'occupied':
        return <Warning color="warning" />;
      case 'disabled':
        return <Block color="error" />;
      default:
        return <Devices />;
    }
  };

  const handleEscritorioChange = (value: number | number[] | null) => {
    if (Array.isArray(value)) {
      setSelectedEscritorioId(typeof value[0] === 'number' ? value[0] : '');
    } else if (typeof value === 'number') {
      setSelectedEscritorioId(value);
    } else {
      setSelectedEscritorioId('');
    }
  };

  const handleClose = () => {
    setSelectedEscritorioId('');
    setSelectedCaixaId('');
    setSelectedPort('');
    setSelectedCircuitoOption(null);
    setCaixas([]);
    setCaixaPorts([]);
    setCircuitos([]);
    setCaixaSearch('');
    setCurrentCaixaCoords(null);
    setNovaCaixaCoords(null);
    setMapModalOpen(false);
    setMapView(null);
    setError(null);
    onClose();
  };

  const handleTrocar = async () => {
    if (!equipment) return;

    if (!selectedCaixaId || !selectedPort) {
      setError('Selecione a CAIXA e a porta de destino');
      return;
    }

    if (
      equipment.caixa_id === selectedCaixaId &&
      equipment.caixa_port_number === selectedPort
    ) {
      setError('Selecione uma CAIXA ou porta diferente da atual');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const payload: {
        caixa_id: number;
        caixa_port_number: number;
        circuito_id?: number;
        circuito_identificacao?: string;
      } = {
        caixa_id: selectedCaixaId as number,
        caixa_port_number: selectedPort as number,
      };

      if (typeof selectedCircuitoOption === 'string' && selectedCircuitoOption.trim()) {
        payload.circuito_identificacao = selectedCircuitoOption.trim();
      } else if (
        selectedCircuitoOption &&
        typeof selectedCircuitoOption === 'object' &&
        'id' in selectedCircuitoOption
      ) {
        payload.circuito_id = selectedCircuitoOption.id;
      }

      const response = await equipmentService.trocarCto(equipment.id, payload);

      if (response.success) {
        onSuccess();
        handleClose();
      } else {
        setError(response.error || response.message || 'Erro ao trocar CTO');
      }
    } catch (err: any) {
      console.error('Erro ao trocar CTO:', err);
      setError(err.response?.data?.error || err.message || 'Erro ao trocar CTO');
    } finally {
      setLoading(false);
    }
  };

  const selectedCaixa = caixas.find(caixa => caixa.id === selectedCaixaId);

  return (
    <>
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ p: 1.5 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            <SwapHoriz color="primary" />
            <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
              Trocar de CTO
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small" sx={{ p: 0.5 }}>
            <Close sx={{ fontSize: '1rem' }} />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 1.5 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 1.5, fontSize: '0.8rem' }}>
            {error}
          </Alert>
        )}

        {loading && <LinearProgress sx={{ mb: 1.5 }} />}

        {equipment && (
          <Card variant="outlined" sx={{ mb: 1.5 }}>
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} mb={0.5}>
                <Typography variant="subtitle2">
                  CTO Atual
                </Typography>
                {currentCaixaCoords && (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<MapIcon sx={{ fontSize: 14 }} />}
                    onClick={() => openMap(currentCaixaCoords)}
                    sx={{ fontSize: '0.75rem', py: 0.25, px: 1, minHeight: 28 }}
                  >
                    Ver no mapa
                  </Button>
                )}
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                <strong>Equipamento:</strong> {equipment.modelo?.titulo || 'N/A'} ({equipment.gpon_sn || 'N/A'})
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                <strong>CAIXA:</strong> {equipment.caixa?.name || equipment.caixa_name || 'N/A'} — <strong>Porta:</strong> {equipment.caixa_port_number || 'N/A'}
              </Typography>
              {equipment.circuito?.identificacao && (
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                  <strong>Circuito:</strong> {equipment.circuito.identificacao}
                </Typography>
              )}
              {equipment.provision_status === 'provisioned' && (
                <Alert severity="info" sx={{ mt: 1, fontSize: '0.75rem', py: 0 }}>
                  Se a nova CTO estiver em OLT/porta diferente, a ONU será desprovisionada automaticamente antes da troca.
                </Alert>
              )}
            </CardContent>
          </Card>
        )}

        <Grid container pt={0.7} spacing={1.5}>
          <Grid item xs={12} md={6}>
            <EscritorioSelect
              value={selectedEscritorioId === '' ? null : selectedEscritorioId}
              onChange={handleEscritorioChange}
              fullWidth
              size="small"
              required
              label="Escritório"
              placeholder="Selecione um escritório"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Selecione a CAIXA</InputLabel>
              <Select
                value={selectedCaixaId}
                onChange={(e) => setSelectedCaixaId(e.target.value as number)}
                label="Selecione a CAIXA"
                disabled={!selectedEscritorioId || loadingCaixas}
                MenuProps={{ autoFocus: false }}
              >
                <MenuItem
                  disableTouchRipple
                  sx={{ cursor: 'default', bgcolor: 'transparent', '&:hover': { bgcolor: 'transparent' }, py: 1, px: 1 }}
                  onKeyDown={(e) => { if (e.key !== 'Tab') e.stopPropagation(); }}
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <TextField
                    size="small"
                    fullWidth
                    autoFocus
                    placeholder="Pesquisar caixa..."
                    value={caixaSearch}
                    onChange={(e) => setCaixaSearch(e.target.value)}
                    onKeyDown={(e) => { if (e.key !== 'Tab') e.stopPropagation(); }}
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search sx={{ fontSize: '1rem' }} />
                        </InputAdornment>
                      ),
                      endAdornment: caixaSearch && (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); setCaixaSearch(''); }}>
                            <Clear sx={{ fontSize: '0.9rem' }} />
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                </MenuItem>

                {loadingCaixas ? (
                  <MenuItem disabled>
                    <Typography sx={{ fontSize: '0.8rem' }}>Carregando caixas...</Typography>
                  </MenuItem>
                ) : filteredCaixas.length === 0 ? (
                  <MenuItem disabled>
                    <Typography sx={{ fontSize: '0.8rem' }}>Nenhuma caixa encontrada</Typography>
                  </MenuItem>
                ) : (
                  filteredCaixas.map((caixa) => (
                    <MenuItem key={caixa.id} value={caixa.id}>
                      <Typography sx={{ fontSize: '0.8rem' }}>
                        {caixa.name} - {caixa.address}
                      </Typography>
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth disabled={!selectedCaixaId || loadingPorts} size="small">
              <InputLabel>Selecione a Porta</InputLabel>
              <Select
                value={selectedPort}
                onChange={(e) => setSelectedPort(e.target.value as number)}
                label="Selecione a Porta"
                size="small"
              >
                {caixaPorts.map((port) => (
                  <MenuItem
                    key={port.port_number}
                    value={port.port_number}
                    disabled={port.status !== 'available'}
                  >
                    <Box display="flex" alignItems="center" gap={0.5}>
                      {getPortIcon(port.status)}
                      <Typography sx={{ fontSize: '0.8rem' }}>
                        {getPortLabel(port)}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <Autocomplete
              freeSolo
              size="small"
              options={circuitos}
              getOptionLabel={(option) => typeof option === 'string' ? option : String((option as Circuito).identificacao)}
              disabled={!selectedCaixaId || loadingCircuitos}
              value={selectedCircuitoOption as any}
              onChange={(_, newValue) => setSelectedCircuitoOption(newValue)}
              onInputChange={(_, newInputValue) => {
                if (!circuitos.find(c => String(c.identificacao) === newInputValue)) {
                  setSelectedCircuitoOption(newInputValue);
                }
              }}
              renderOption={(props, option) => {
                const { key, ...optionProps } = props as any;
                const isString = typeof option === 'string';
                const label = isString ? option : String((option as Circuito).identificacao);
                const end = !isString ? (option as Circuito).endereco : '';
                return (
                  <li {...optionProps} key={key || (isString ? option : (option as Circuito).id)}>
                    <Box>
                      <Typography variant="body2">{label}</Typography>
                      {end && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: -0.5 }}>
                          {end}
                        </Typography>
                      )}
                    </Box>
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Circuito (Opcional)"
                  placeholder={loadingCircuitos ? 'Carregando...' : 'Selecione ou digite um novo...'}
                />
              )}
            />
          </Grid>

          {selectedCaixa && (
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} mb={0.5}>
                    <Typography variant="subtitle2">
                      Nova CAIXA Selecionada
                    </Typography>
                    {novaCaixaCoords && (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<MapIcon sx={{ fontSize: 14 }} />}
                        onClick={() => openMap(novaCaixaCoords)}
                        sx={{ fontSize: '0.75rem', py: 0.25, px: 1, minHeight: 28 }}
                      >
                        Ver no mapa
                      </Button>
                    )}
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                    <strong>Nome:</strong> {selectedCaixa.name} — <strong>Portas:</strong> {selectedCaixa.capacidade} — <strong>Endereço:</strong> {selectedCaixa.address}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 1.5 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleTrocar}
          disabled={loading || !selectedCaixaId || !selectedPort}
          startIcon={<SwapHoriz />}
        >
          Trocar
        </Button>
      </DialogActions>
    </Dialog>

    {mapView && (
      <Dialog
        open={mapModalOpen}
        onClose={() => setMapModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5, px: 2 }}>
          <Typography variant="subtitle1" fontWeight={700}>
            Localização — {mapView.name}
          </Typography>
          <IconButton onClick={() => setMapModalOpen(false)} size="small">
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 2, pb: 2, pt: 0 }}>
          <CaixaMapComponent
            latitude={mapView.latitude}
            longitude={mapView.longitude}
            caixaName={mapView.name}
            address={mapView.address}
            height={380}
          />
          <Box display="flex" justifyContent="center" mt={1}>
            <Typography variant="caption" color="text.secondary" textAlign="center">
              {mapView.latitude.toFixed(6)}, {mapView.longitude.toFixed(6)}
              {' • '}
              <Button
                size="small"
                variant="text"
                onClick={() => {
                  const url = `https://www.google.com/maps?q=${mapView.latitude},${mapView.longitude}`;
                  window.open(url, '_blank', 'noopener,noreferrer');
                }}
                sx={{ textTransform: 'none', p: 0, minWidth: 'auto', fontSize: 'inherit', verticalAlign: 'baseline' }}
              >
                Abrir no Google Maps
              </Button>
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    )}
    </>
  );
};

export default TrocarCtoModal;
